import { createLogic } from "redux-logic";
import {
    type StartEtaLoadType,
    type FinishEtaLoadType,
    type StartEtaRateFetchType,
    START_ETA_LOAD,
    finishEtaLoad,
    resetEta,
    FINISH_ETA_LOAD,
    startEtaRateFetch,
    finishEtaRateFetch,
    START_ETA_RATE_FETCH,
} from "../actions/EtaActions";
import Debug from "../../../../../common-deprecated/Debug";
import { fetchMonthlyRate } from "../../utils/FinanceInsuranceRate";
import type { BuildAndBuyLogicType } from "../store";
import type { AlternativeType } from "../../../../../common-deprecated/types/CommonTypes";
import { addErrorLog, addRequestLog } from "../../../../../common-deprecated/redux/actions/UtilActions";
import { getFetcher } from "../../../../../common-deprecated/api-clients/ApiClient";
import etaClient from "../../../../shared-logic/api-clients/eta";
import { showFinanceInsurance } from "../../utils/financeInsuranceUtils";

const startEtaLoad = (createLogic as BuildAndBuyLogicType<StartEtaLoadType>)({
    type: START_ETA_LOAD,

    validate({ getState, action }, allow, reject) {
        const { eta, bnbSettings } = getState();

        if (eta.loading || !bnbSettings.showETA) {
            (reject as () => void)();
        } else {
            allow(action);
        }
    },

    async process({ getState }, dispatch, done) {
        try {
            const { car, basket, commonSettings, bnbSettings } = getState();

            const fetcher = getFetcher(etaClient, {
                commonSettings,
                model: car.model.id,
                car: car.id,
                colour: basket.colour.internalCode,
                upholstery: basket.upholstery.internalCode,
                etaMatchingType: bnbSettings.summaryEtaMatchingType,
            });

            const etaConfig = await fetcher();

            const { fetchResult, reqLogs, errorLogs } = etaConfig;

            if (reqLogs?.length) dispatch(addRequestLog(reqLogs));
            if (errorLogs?.length) dispatch(addErrorLog(errorLogs));

            // add price of Colour and Upholstery to the total price
            fetchResult.alternatives.forEach((alt: AlternativeType) => {
                alt.price.cash += alt.colour.price.cash + alt.upholstery.price.cash;
                alt.price.discount += alt.colour.price.discount + alt.upholstery.price.discount;
            });
            dispatch(finishEtaLoad(fetchResult));
        } catch (exception) {
            Debug.error("Failed to get ETA", exception);
            dispatch(resetEta(true));
        }

        done();
    },
});

const finishEtaLoadLogic = (createLogic as BuildAndBuyLogicType<FinishEtaLoadType>)({
    type: FINISH_ETA_LOAD,
    // eslint-disable-next-line no-empty-pattern
    process({}, dispatch, done) {
        dispatch(startEtaRateFetch());
        done();
    },
});

const startEtaRateFetchLogic = (createLogic as BuildAndBuyLogicType<StartEtaRateFetchType>)({
    type: START_ETA_RATE_FETCH,
    async process({ getState }, dispatch, done) {
        try {
            const { eta, commonSettings, bnbSettings, finance, navigation, car } = getState();
            const { brand, country, language } = commonSettings;

            const showFinance = showFinanceInsurance({
                type: "finance",
                bnbSettings,
                car,
                commonSettings,
                finance,
                step: navigation.step,
            });

            if (showFinance) {
                const monthlyRates = await Promise.all(
                    eta.alternatives.map(async (altCar) => {
                        const config = {
                            CarID: altCar.id,
                            ExteriorColourID: altCar.colour.id,
                            ModelID: altCar.model.id,
                            UpholsteryID: altCar.upholstery.id,
                            Brand: brand,
                            Country: country,
                            Language: language,
                            Options: [],
                            // upholstery and colour price is already included here
                            TotalPrice: altCar.price.cash,
                        };

                        const result = await fetchMonthlyRate(commonSettings, bnbSettings, config);
                        return result;
                    }),
                );

                const newAlternatives = eta.alternatives.map((alternative, index) => {
                    const price = {
                        ...alternative.price,
                        monthly: Number(monthlyRates[index].total),
                    };

                    return {
                        ...alternative,
                        price,
                        financeInfo: monthlyRates[index].info,
                    };
                });
                dispatch(finishEtaRateFetch(newAlternatives));
            }
            dispatch(finishEtaRateFetch(eta.alternatives));
        } catch (exception) {
            Debug.error("Failed to get MONTHLY RATES", exception);
        }
        done();
    },
});

export default [startEtaLoad, finishEtaLoadLogic, startEtaRateFetchLogic];
